<template>
  <div class="product-row">
    <div class="product-row-header">
      <span class="product-row-label">{{ t('operationProductRow.productLabel', { n: index + 1 }) }}</span>
      <button v-if="canRemove" class="remove-btn" @click="$emit('remove')" :title="t('operationProductRow.removeTitle')">
        &times;
      </button>
    </div>

    <div class="product-row-fields">
      <!-- Selezione prodotto (full width) -->
      <div class="field-group field-product">
        <label>{{ t('operationProductRow.productFieldLabel') }}</label>
        <div class="product-select-row">
          <select v-model="localData.id_product" @change="onProductChange" class="form-control">
            <option value="">{{ t('operationProductRow.selectPlaceholder') }}</option>
            <option v-for="p in products" :key="p.id_product" :value="p.id_product">
              {{ p.name }}
            </option>
            <option value="xxx">{{ t('operationProductRow.otherManual') }}</option>
          </select>
          <button type="button" @click="$emit('create-product')" class="create-product-btn" :title="t('operationProductRow.createProductTitle')">+</button>
        </div>
      </div>

      <!-- Nome prodotto manuale (full width) -->
      <div v-if="localData.id_product === 'xxx'" class="field-group field-product-name">
        <label>{{ t('operationProductRow.productNameLabel') }}</label>
        <input v-model="localData.product_name" type="text" class="form-control" :placeholder="t('operationProductRow.productNamePlaceholder')" />
      </div>

      <!-- Quantità totale + UdM sempre sulla stessa riga (flex) -->
      <div class="field-qty-uom">
        <div class="field-group">
          <label>{{ t('operationProductRow.totalLabel') }}</label>
          <input v-model.number="localData.amount_tot" type="number" step="0.01" class="form-control"
            @input="recalcAmounts('tot')" />
        </div>
        <div class="field-group field-uom">
          <label>{{ t('operationProductRow.unitLabel') }}</label>
          <select v-model="localData.uom" @change="recalcAmounts('tot')" class="form-control">
            <option value="kg">kg</option>
            <option value="q">q</option>
            <option value="t">t</option>
            <option value="g">g</option>
            <option value="l">l</option>
            <option value="ml">ml</option>
            <option value="n">n°</option>
          </select>
        </div>
      </div>

      <!-- Qtà/ha (col-6) -->
      <div class="field-group">
        <label>{{ t('operationProductRow.qtyHaLabel') }}</label>
        <input v-model.number="localData.amount_ha" type="number" step="0.01" class="form-control"
          @input="recalcAmounts('ha')" />
      </div>
    </div>

    <!-- Riga info: scorte / dose per litro / rame -->
    <div v-if="stockInfo || dosePerLiter || cuSummary" class="product-info-row" :style="{ gridTemplateColumns: infoRowCols }">
      <div v-if="stockInfo" class="stock-info" :class="{ 'stock-insufficient': stockInfo.insufficient }">
        <span v-if="stockInfo.insufficient">⚠ {{ t('operationProductRow.stockInsufficientPrefix') }}</span>
        <span v-else>{{ t('operationProductRow.stockPrefix') }}</span>
        <strong>{{ stockInfo.residual.toFixed(2) }} {{ stockInfo.uom }}</strong>
      </div>
      <div v-if="dosePerLiter" class="dose-per-liter-info">
        {{ dosePerLiter.value }} {{ dosePerLiter.unit }}
      </div>
      <div v-if="cuSummary" class="cu-summary" :class="{ 'cu-warn': cuSummary.warn, 'cu-over-limit': cuSummary.overLimit }">
        <span v-if="cuSummary.hasAcc">
          Cu: {{ cuSummary.cuAcc }}+{{ cuSummary.cuThis }} = <strong>{{ cuSummary.cuTotal }} kg/ha</strong>
        </span>
        <span v-else>Cu: <strong>{{ cuSummary.cuThis }} kg/ha</strong></span>
        <span v-if="cuSummary.overLimit"> ⚠ &gt;4</span>
        <span v-else-if="cuSummary.warn"> ⚠ &gt;1</span>
      </div>
    </div>

    <!-- Dosi NPK per concimazione (crop_operation 3) -->
    <div v-if="isFertilization" class="npk-section">
      <div class="npk-row">
        <div class="field-group field-npk">
          <label>N %</label>
          <input v-model.number="localData.dose_n" type="number" step="0.01" class="form-control"
            @input="updateNpkCalc" />
        </div>
        <!-- <div class="field-group field-npk">
          <label>NH4 %</label>
          <input v-model.number="localData.dose_nh4" type="number" step="0.01" class="form-control"
            @input="updateNpkCalc" />
        </div>
        <div class="field-group field-npk">
          <label>Urea %</label>
          <input v-model.number="localData.dose_urea" type="number" step="0.01" class="form-control"
            @input="updateNpkCalc" />
        </div> -->
        <div class="field-group field-npk">
          <label>P %</label>
          <input v-model.number="localData.dose_p" type="number" step="0.01" class="form-control"
            @input="updateNpkCalc" />
        </div>
        <div class="field-group field-npk">
          <label>K %</label>
          <input v-model.number="localData.dose_k" type="number" step="0.01" class="form-control"
            @input="updateNpkCalc" />
        </div>
      </div>
      <div v-if="npkSummary" class="npk-summary">
        {{ npkSummary }}
      </div>
      <div v-if="npkMissing" class="npk-warning">
        {{ t('operationProductRow.npkMissingWarning') }}
      </div>
    </div>

    <!-- Avversità trattate (crop_operation 10) -->
    <div v-if="isTreatment" class="reason-section">
      <label class="reason-label">{{ t('operationProductRow.reasonLabel') }}</label>
      <PestTagger
        v-model="localData.pests"
        :crop-eppo-codes="cropEppoCodes"
      />
    </div>

  </div>
</template>

<script setup>
import { reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { UOM_MULTIPLIER } from '../composables/useWarehouse'
import PestTagger from './PestTagger.vue'

const { t } = useI18n()

const props = defineProps({
  index: { type: Number, required: true },
  products: { type: Array, default: () => [] },
  modelValue: { type: Object, default: () => ({}) },
  cropOperationType: { type: [Number, String], required: true },
  totalArea: { type: Number, default: 0 },
  volumeHa: { type: Number, default: 0 },
  canRemove: { type: Boolean, default: true },
  cropEppoCodes: { type: Array, default: () => [] },
  productResiduals: { type: Object, default: () => ({}) },
  cuAccumulated: { type: Number, default: 0 },
})

const emit = defineEmits(['update:modelValue', 'remove', 'create-product'])

const localData = reactive({
  id_product: '',
  product_name: '',
  uom: 'kg',
  amount_tot: null,
  amount_ha: null,
  dose_n: 0,
  dose_p: 0,
  dose_k: 0,
  pests: [],
  // Coefficienti di disponibilità azoto pluriennale (solo effluenti di allevamento)
  org_type: null,
  org_nitrogen_conc: null,
  org_nitrogen_available: null,
  n_min_first_year: null,
  org_nitrogen_available_first_year: null,
  ...props.modelValue
})

const isFertilization = computed(() => parseInt(props.cropOperationType) === 3)
const isTreatment     = computed(() => parseInt(props.cropOperationType) === 10)

// Dose per litro di acqua
// UOM_MULTIPLIER → kg (solidi) o l (liquidi); *1000 per avere g/l o ml/l
const dosePerLiter = computed(() => {
  if (!isTreatment.value || !props.volumeHa || props.volumeHa <= 0) return null
  const amountHa = parseFloat(localData.amount_ha) || 0
  if (amountHa <= 0) return null
  const mult = UOM_MULTIPLIER[localData.uom] || 1
  const baseKgOrL = amountHa * mult       // kg/ha o l/ha
  const dpl = (baseKgOrL * 1000) / props.volumeHa  // g/l o ml/l
  const isLiquid = localData.uom === 'l' || localData.uom === 'ml'
  return { value: dpl.toFixed(2), unit: isLiquid ? 'ml/l' : 'g/l' }
})

const stockInfo = computed(() => {
  if (!localData.id_product || localData.id_product === 'xxx') return null
  const residual = props.productResiduals?.[localData.id_product]
  if (residual == null) return null
  const product = props.products.find(p => p.id_product === localData.id_product)
  const uom = product?.uom || localData.uom || ''
  const amountTot = parseFloat(localData.amount_tot) || 0
  const stockMult = UOM_MULTIPLIER[product?.uom] || 1
  const opMult = UOM_MULTIPLIER[localData.uom] || 1
  const insufficient = amountTot > 0 && (amountTot * opMult) > (residual * stockMult)
  return { residual, uom, insufficient }
})

const cuSummary = computed(() => {
  if (!isTreatment.value) return null
  const product = props.products.find(p => p.id_product === localData.id_product)
  if (!product?.cu_perc || product.cu_perc <= 0) return null
  const amountHa = parseFloat(localData.amount_ha) || 0
  if (amountHa <= 0) return null
  const mult = UOM_MULTIPLIER[localData.uom] || 1
  const cuThis = amountHa * mult * product.cu_perc / 100
  const cuTotal = (props.cuAccumulated || 0) + cuThis
  return {
    cuThis: cuThis.toFixed(3),
    cuTotal: cuTotal.toFixed(3),
    hasAcc: (props.cuAccumulated || 0) > 0,
    cuAcc: (props.cuAccumulated || 0).toFixed(3),
    warn: cuTotal > 1,
    overLimit: cuTotal > 4,
  }
})

// totalArea è in m² (dal plot), convertiamo in ha per i calcoli /ha
const areaHa = computed(() => {
  if (!props.totalArea || props.totalArea <= 0) return 0
  return props.totalArea / 10000
})

const npkMissing = computed(() => {
  if (!isFertilization.value) return false
  if (!localData.id_product || localData.id_product === 'xxx') return false
  return !localData.dose_n && !localData.dose_p && !localData.dose_k
})

const npkSummary = computed(() => {
  if (!isFertilization.value) return ''
  const amountHa = parseFloat(localData.amount_ha) || 0
  if (amountHa <= 0) return t('operationProductRow.npkEnterQuantity')

  const mult = UOM_MULTIPLIER[localData.uom] || 1
  const n = parseFloat(localData.dose_n) || 0
  const p = parseFloat(localData.dose_p) || 0
  const k = parseFloat(localData.dose_k) || 0
  const nTotal = (mult * amountHa * n / 100).toFixed(1)
  const pTotal = (mult * amountHa * p / 100).toFixed(1)
  const kTotal = (mult * amountHa * k / 100).toFixed(1)

  return t('operationProductRow.npkSummary', { n: nTotal, p: pTotal, k: kTotal })
})

function onProductChange() {


  // debugger;

  if (localData.id_product === 'xxx') {
    localData.product_name = ''
    localData.dose_n = 0
    localData.dose_p = 0
    localData.dose_k = 0
    resetOrgCoefficients()
    return
  }

  const product = props.products.find(p => p.id_product === localData.id_product)
  if (product) {
    localData.product_name = product.name || ''
    if (product.uom && !localData.amount_ha && !localData.amount_tot) localData.uom = product.uom
    if (isFertilization.value) {
      localData.dose_n = parseFloat(product.n) || 0
      localData.dose_p = parseFloat(product.p) || 0
      localData.dose_k = parseFloat(product.k) || 0
      // Effluenti di allevamento: porta i coefficienti di disponibilità N pluriennale
      // (da aepest.ferti.json, copiati sul prodotto alla selezione dal catalogo) nell'operazione
      if (product.ferti_json?.org_type) {
        localData.org_type = product.ferti_json.org_type ?? null
        localData.org_nitrogen_conc = product.ferti_json.org_nitrogen_conc ?? null
        localData.org_nitrogen_available = product.ferti_json.org_nitrogen_available ?? null
        localData.n_min_first_year = product.ferti_json.n_min_first_year ?? null
        localData.org_nitrogen_available_first_year = product.ferti_json.org_nitrogen_available_first_year ?? null
      } else {
        resetOrgCoefficients()
      }
    }
  }
}

function resetOrgCoefficients() {
  localData.org_type = null
  localData.org_nitrogen_conc = null
  localData.org_nitrogen_available = null
  localData.n_min_first_year = null
  localData.org_nitrogen_available_first_year = null
}

function recalcAmounts(source) {
  if (areaHa.value <= 0) return

  if (source === 'tot' && localData.amount_tot != null) {
    localData.amount_ha = parseFloat((localData.amount_tot / areaHa.value).toFixed(2))
  } else if (source === 'ha' && localData.amount_ha != null) {
    localData.amount_tot = parseFloat((localData.amount_ha * areaHa.value).toFixed(2))
  }
}

function updateNpkCalc() {
  // Il computed npkSummary si aggiorna automaticamente
  // Emettiamo solo l'update
  emitUpdate()
}

const infoRowCols = computed(() => {
  const hasCu = !!cuSummary.value
  const hasDpl = !!dosePerLiter.value
  const hasStock = !!stockInfo.value
  if (hasCu && hasDpl && hasStock) return '2fr 1fr 1fr'
  if (hasCu && hasStock) return '1fr 1fr'
  if (hasDpl && hasStock) return '1fr 1fr'
  return '1fr'
})

function emitUpdate() {
  emit('update:modelValue', { ...localData })
}

// Watch per emettere i cambiamenti
watch(localData, () => {
  emitUpdate()
}, { deep: true })

// Sync con modelValue esterno
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    Object.assign(localData, newVal)
  }
}, { deep: true })
</script>

<style scoped>
.product-row {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fafbfc;
}

.product-row-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.product-row-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #4a5568;
}

.remove-btn {
  background: none;
  border: none;
  color: #e53e3e;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
}

.remove-btn:hover {
  color: #c53030;
}

.product-row-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  align-items: end;
}

.field-group label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #718096;
  margin-bottom: 0.2rem;
}

.form-control {
  width: 100%;
  padding: 0.4rem 0.5rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: inherit;
}

.form-control:focus {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.15);
}

.product-select-row {
  display: flex;
  gap: 0.4rem;
  align-items: stretch;
}

.product-select-row select {
  flex: 1;
}

.create-product-btn {
  background: #4299e1;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
}

.create-product-btn:hover {
  background: #3182ce;
}

.field-product,
.field-product-name {
  grid-column: 1 / -1;
}

.field-qty-uom {
  display: flex;
  gap: 0.5rem;
  align-items: end;
}

.field-qty-uom .field-group:first-child {
  flex: 1;
}

.field-uom {
  width: 70px;
  flex-shrink: 0;
}

.npk-section {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}

.npk-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.npk-summary {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2b6cb0;
  background: #ebf8ff;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
}

.npk-warning {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  color: #7b4c00;
  background: #fffbeb;
  border: 1px solid #f6c000;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
}

.reason-section {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
}

.reason-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #718096;
  margin-bottom: 0.35rem;
}

.product-info-row {
  display: flex;
  gap: 0.4rem;
  margin-top: 0.4rem;
  flex-wrap: wrap;
}

.product-info-row {
  display: grid;
  gap: 0.4rem;
  margin-top: 0.4rem;
}

.stock-info,
.cu-summary,
.dose-per-liter-info {
  font-size: 0.8rem;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
}

.stock-info {
  color: #276749;
  background: #f0fff4;
}

.dose-per-liter-info {
  color: #2b4c7e;
  background: #ebf4ff;
  font-weight: 500;
  text-align: center;
}

.stock-info.stock-insufficient {
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  font-weight: 600;
}

.cu-summary.cu-warn {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  font-weight: 600;
}

.cu-summary.cu-over-limit {
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  font-weight: 600;
}

@media (max-width: 600px) {
  .product-row-fields {
    grid-template-columns: 1fr;
  }

  .field-qty-uom,
  .field-group {
    grid-column: 1 / -1;
  }

  .npk-row {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
